import { Dayjs } from 'dayjs'
import { useContext, useMemo } from 'react'
import { electricTaxPerKwh } from '../../../../constants'
import useUser from '../../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../../contexts/objectContext/ObjectContext'
import { trpc } from '../../../../modules/trpc'
import { TimespanOption } from '../../../electricity/chartUtils/hooks/useChartTime'

export type SolarPanelHook = ReturnType<typeof useSolarPanel>

interface Time {
	span: TimespanOption
	startTime?: Dayjs
	endTime?: Dayjs
}

export const useSolarPanel = (time: Time, inverterIds: string[] = []) => {
	const { user } = useUser()

	const object = useContext(ObjectContext)

	const { data: inverter, isFetching: inverterLoading } = trpc.eNode.inverter.getAll.useQuery(
		{
			hashedPid: user!.hashedPid,
		},
		{
			enabled: user?.panelLinked && Boolean(user?.hashedPid),
			staleTime: 1000 * 60 * 60, // 1 hour
		}
	)

	const { data: inverterStatistics, isFetching: inverterStatisticsLoading } =
		trpc.eNode.inverter.getInverterStatisticsV2.useQuery(
			{
				hashedPid: user!.hashedPid,
				startDate: time.startTime?.startOf('hour').toISOString() || '',
				endDate: time.endTime?.endOf('hour').toISOString() || '',
				inverterIds: inverterIds,
				resolution: time.span === 'day' ? 'HOUR' : 'DAY',
				aggregate: time.span === 'day' ? 'hour' : time.span === 'year' ? 'year' : 'month',
			},
			{
				enabled: user?.panelLinked && !!time.startTime && !!time.endTime,
				staleTime: 1000 * 60 * 60, // 1 hour
			}
		)

	const { data: electricityEarnings, isFetching: electricityEarningsLoading } =
		trpc.eNode.inverter.getElectricityEarnings.useQuery(
			{
				startDate: time.startTime?.tz('Europe/Helsinki')?.startOf('hour')?.toISOString() || '',
				endDate: time.endTime?.tz('Europe/Helsinki')?.endOf('hour')?.toISOString() || '',
				hashedPid: user?.hashedPid || '',
				objectId: object.id,
			},
			{
				enabled: user?.panelLinked && !!time.startTime && !!time.endTime && !!object.id,
				staleTime: 1000 * 60 * 60, // 1 hour
			}
		)

	const savings = useMemo(() => {
		if (!electricityEarnings?.total?.length) return 0

		const margin = object.spotMarginal || 0

		const dayTransfer =
			object.electricityTransferPriceContractDayPrice ?? object.electricityTransferPricePerKwh ?? 0
		const nightTransfer =
			object.electricityTransferPriceContractNightPrice ??
			object.electricityTransferPricePerKwh ??
			0

		const dayWithTax = object.electricityTaxIncluded ? dayTransfer : dayTransfer + electricTaxPerKwh
		const nightWithTax = object.electricityTaxIncluded
			? nightTransfer
			: nightTransfer + electricTaxPerKwh

		const marginEuros = margin / 100
		const dayEuros = dayWithTax / 100
		const nightEuros = nightWithTax / 100

		let totalSavings = 0

		if (time.span === 'day') {
			electricityEarnings.total.forEach(entry => {
				const isNight = (() => {
					const hour = new Date(entry.date).getHours()
					return hour < 7 || hour >= 22
				})()

				const transferCost = entry.kwh * (isNight ? nightEuros : dayEuros)
				const marginCost = entry.kwh * marginEuros

				totalSavings += entry.earnings + transferCost + marginCost
			})
		} else {
			electricityEarnings.total.forEach(entry => {
				const transferCost = entry.kwh * ((9 / 24) * nightEuros + (15 / 24) * dayEuros)
				const marginCost = entry.kwh * marginEuros

				totalSavings += entry.earnings + transferCost + marginCost
			})
		}

		return totalSavings
	}, [
		electricityEarnings,
		object.spotMarginal,
		object.electricityTransferPriceContractDayPrice,
		object.electricityTransferPriceContractNightPrice,
		object.electricityTransferPricePerKwh,
		object.electricityTaxIncluded,
		time.span,
	])

	return {
		inverter: inverter?.[0],
		inverterStatistics,
		electricityEarnings,
		savings,
		productionAuthorized: Boolean(object.synerallProductionId) || Boolean(object.productionGsrn),
		isLoading: inverterStatisticsLoading || inverterLoading || electricityEarningsLoading,
	}
}
